/**
 * AppShell — the 240px sidebar + 56px topbar frame every in-app screen sits in
 * (Figma `01/ Entry/ Dashboard`, node 12:907: `Shell/Sidebar` 240×1080 +
 * `Main` 1680 with `Shell/TopBar` 56 and `Content` below).
 *
 * The breadcrumb is derived from the nav registry rather than passed down by each
 * page: the registry already knows which SECTION an item belongs to, which is
 * exactly what Figma's breadcrumb shows ("Pipeline / Dashboard"). Deriving it
 * means a page cannot label itself something the sidebar disagrees with.
 *
 * `PageHeader` is exported from here because it is shell furniture, not dashboard
 * furniture: Figma's `Page header` (12:1204) sits inside `Content` on every
 * screen of the design.
 */

import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { ACCEPTANCE_NAV_SECTION, ADMIN_NAV_SECTION, NAV_SECTIONS } from '../../domain/nav';
import type { NavAuthority } from '../../domain/nav';
import { useSession } from '../../auth/session';
import { useCountryCode } from '../../auth/guards';
import { ProfileMenu } from './ProfileMenu';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';

/** ['Pipeline', 'Dashboard'] for `/countries/IN`, from the nav registry. */
function breadcrumbFor(pathname: string): readonly string[] {
  const parts = pathname.split('/').filter((part) => part !== '');
  const segment = parts[2] ?? '';
  // The full remainder too: admin paths are two segments ('admin/agent-keys'),
  // so `segment` alone would breadcrumb every admin screen as 'admin'. The
  // prefix branch keeps a DETAIL page under its list's crumb — 'admin/users/7'
  // is still ['Admin', 'Users'], the way 'inquiries/42' stays under Inquiries
  // (that one happens to work via `segment`; nested admin paths need the
  // explicit prefix test).
  const remainder = parts.slice(2).join('/');
  /*
   * v21 · UNIT C — the AUTHORED Acceptance section is consulted FIRST, and the
   * order is load-bearing: its one path (`signoffs/acceptance-chase`) NESTS
   * under a Figma item's path, and this loop returns on its first match — with
   * the Figma sections first, `segment === 'signoffs'` would crumb the chase
   * as "Deployment / Sign Offs", the register it deliberately is not (measured
   * on the live shell before this line moved). For plain `/signoffs` the chase
   * item matches none of the three tests, so the queue's own crumb is
   * untouched. The OTHER authored sections stay out of this loop as before —
   * none of their paths nests under a Figma row, so they keep the plain
   * fallback rather than inheriting a decision nobody made here.
   */
  for (const section of [ACCEPTANCE_NAV_SECTION, ...NAV_SECTIONS, ADMIN_NAV_SECTION]) {
    for (const item of section.items) {
      if (
        item.path === segment ||
        item.path === remainder ||
        (item.path !== '' && remainder.startsWith(`${item.path}/`))
      ) {
        return [section.title, item.label];
      }
    }
  }
  // An unregistered path. Say so plainly rather than guessing a label — an
  // invented breadcrumb is how a wrong URL comes to look like a real page.
  return ['Global One', segment === '' ? 'Dashboard' : segment];
}

export function AppShell(): React.JSX.Element {
  /*
   * v22-A (part 2 of 2) — THE DOCUMENT ITSELF IS LOCKED WHILE THE SHELL IS
   * MOUNTED. The frame clip below is not sufficient on its own, and that is a
   * MEASUREMENT, not a theory: with the frame at `h-dvh overflow-hidden` and
   * every real scroller constrained, `document.scrollingElement.scrollHeight`
   * still read 1633 in a 500px viewport and `window.scrollTo(0, 800)` really
   * moved the page — Chromium propagates a NESTED scroller's layout overflow
   * into the ICB's scrollable overflow straight through intermediate clips.
   * `tests/shell-width.spec.ts` documents the identical quirk horizontally
   * (documentElement.scrollWidth 1288 while the wrapper's own was 1080/990).
   * ⚠ AND `clip` IS A BELT, NOT THE FIX — over-claiming it was measured
   * wrong. The CSS Overflow spec makes the viewport translate a root
   * `clip`/`visible` to `hidden`/`auto`, and a HIDDEN viewport keeps its
   * scroll container: `window.scrollTo` moved a clip-rooted document 800px in
   * a real Chrome window. What this lock genuinely buys is the USER-FACING
   * half — no scrollbar and no wheel/gesture scroll — while the load-bearing
   * fix is the frame's `relative` (see the frame comment: it removes the
   * scrollable overflow itself, so there is nothing left to scroll by any
   * means). The lock is held for exactly as long as the shell is mounted,
   * and released on unmount — the login screen's EntryBackdrop
   * (`min-h-dvh`, centred) keeps document scroll on short viewports, which a
   * global `html { overflow: hidden }` would have taken from it.
   */
  useEffect(() => {
    const root = document.documentElement;
    const previous = root.style.overflow;
    root.style.overflow = 'clip';
    return () => {
      root.style.overflow = previous;
    };
  }, []);

  const countryCode = useCountryCode();
  // `me` is no longer read here: the identity block is `ProfileMenu`, which
  // reads the session itself.
  const { accessibleCountries, isGroupAdmin, permsIn, signOut } = useSession();
  const location = useLocation();

  /**
   * `permsAnywhere` is the flattened union over every accessible country — the
   * gate for `global: true` nav items (Users). Computed inline rather than
   * memoised: five countries × ≤35 keys is not a cost, and a memo would need
   * `me` in its deps anyway.
   */
  const permsAnywhere = accessibleCountries.flatMap((code) => permsIn(code));
  const authority: NavAuthority = {
    isGroupAdmin,
    perms: permsIn(countryCode),
    permsAnywhere,
  };
  const handleSignOut = (): void => {
    void signOut();
  };

  return (
    /*
     * WEB TIER A · WT1 — the ground is the CANVAS, not `bg/page`. In light mode
     * `bg/page` and `bg/surface` are both #ffffff, so every card was 1.00:1
     * against its own page and the app read as flat; the authored canvas
     * (#eef1f6, D3 as amended by MC-12) sits 1.13:1 under a white card — the
     * ground recedes and the cards become figures. In dark, `bg-canvas`
     * resolves to the shipped `bg/page` #161616 (no invented dark half), so
     * this line renders IDENTICALLY to the old `bg-page` there.
     */
    /*
     * v22-A — THE FRAME IS THE VIEWPORT, AND `main` IS THE SCROLLER.
     *
     * This line was `min-h-dvh`, which made the frame GROW with the page and
     * left the DOCUMENT as the scroll container — so on any page taller than
     * the viewport the whole flex row scrolled, sidebar included. The owner
     * hit it in production: mid-scroll on field-settings, `CRFs` sat at the
     * top of the nav with every heading above it gone. The sidebar has had an
     * internal nav scroller all along (`Sidebar.tsx`, the `overflow-y-auto`
     * region); it was built to hold position and then placed in a frame that
     * carried it away.
     *
     * `h-dvh overflow-hidden` makes the frame exactly one viewport; the shell
     * chrome (sidebar AND top bar — pinning both is the same one fix) holds
     * position and `main` below scrolls its own content. Sticky footers and
     * the JumpNav's scrollIntoView key off the nearest scroll container, so
     * they follow `main` unchanged; nothing in the app reads window.scrollY
     * (measured: zero hits). Route changes keep main's scroll exactly as the
     * document scroller kept it before — same behaviour, different element.
     *
     * `relative` IS LOAD-BEARING, NOT DECORATION. Measured in a real,
     * un-emulated Chrome window: with the frame clipped and every scroller
     * constrained, the document still scrolled 800px — to
     * `scrollingElement.scrollHeight` 1631 — because the nav rows' `sr-only`
     * labels are `position: absolute` and NO ancestor in the sidebar chain was
     * positioned, so their containing block was the ICB itself. An
     * absolutely-positioned box escapes every overflow clip BETWEEN it and its
     * containing block, so each label contributed its static position —
     * laid-out nav coordinates down to ~1631 — straight to the document's
     * scrollable overflow. `relative` here makes the frame the containing
     * block for every unanchored absolute descendant (any page's, not only
     * the sidebar's), which puts them back inside this clip and removes the
     * document's overflow at the source. Verified: a synthetic twin of this
     * layout WITH a positioned frame measures scrollHeight == clientHeight
     * and window.scrollTo cannot move it.
     */
    <div className="relative flex h-dvh overflow-hidden bg-canvas">
      <Sidebar
        countryCode={countryCode}
        authority={authority}
        userBlock={<ProfileMenu />}
      />
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar
          breadcrumb={breadcrumbFor(location.pathname)}
          countryCode={countryCode}
          accessibleCountries={accessibleCountries}
          onSignOut={handleSignOut}
        />
        {/*
         * `overflow-x-auto`, NOT `overflow-x-hidden`.
         *
         * ===================================================================
         * WHAT `overflow-x-hidden` WAS DOING, MEASURED
         * ===================================================================
         * It did not stop content from being too wide; it stopped anyone from
         * SEEING that it was. Measured on this branch's parent, real API, real
         * Vite, Chromium:
         *
         *   · CRF record  @ 640px — main.scrollWidth 402 vs clientWidth 400
         *   · CRF list    @ 431px — main.scrollWidth 266 vs clientWidth 191
         *   · Dashboard   @ 375px — main.scrollWidth 162 vs clientWidth 135
         *
         * Every one of those deltas was painted nowhere and reachable by
         * nothing: `overflow-x: hidden` clips WITHOUT offering a scrollbar, so
         * the 75px missing from the CRF list toolbar at 431px could not be got
         * at by scrolling, by keyboard, or by widening anything but the window.
         *
         * The swap costs no containment. `overflow-x` was already NOT `visible`
         * here, which per CSS makes the other axis compute to `auto` — measured
         * `getComputedStyle(main).overflowY === 'auto'` BEFORE this change, so
         * `main` was already a scroll container and already the containing block
         * for its own scrolling. `auto` changes exactly one thing: when the
         * content genuinely does not fit, a scrollbar appears and the content
         * can be reached. Nothing that fits gains a scrollbar.
         *
         * ⚠ NOT `contain: paint`, which was measured as the only thing that also
         * stops a `min-w-[…]` register from growing `document.scrollWidth`
         * (Chromium propagates a nested scroller's layout overflow into the
         * ICB: the CRF list measured 1288 while the table's own wrapper measured
         * 1080/990 and scrolled correctly). It would make `main` the containing
         * block for `position: fixed` descendants, and
         * `features/inquiries/BulkBar.tsx` and `features/inquiries/toast.tsx`
         * are fixed and NOT portalled — both would re-anchor to a column taller
         * than the viewport and land off-screen at the bottom of a long page.
         * The register's own `overflow-x-auto` already makes the table
         * reachable, so `tests/shell-width.spec.ts` asserts that the SHELL
         * contributes no page overflow rather than pretending a 1080px table
         * fits in 431px.
         */}
        {/* v22-A: `overflow-y-auto` — main is the page scroller now (see the
          * frame comment above). Its automatic flex min-size does not apply
          * once overflow is non-visible, so no min-h-0 is needed for it to
          * shrink below its content and actually scroll. */}
        <main className="min-w-0 flex-1 overflow-x-auto overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/**
 * PageHeader — Figma `Page header` (12:1204): a `H1/24` greeting on the left and a
 * controls group on the right, at px-24/py-24.
 *
 * ===========================================================================
 * FIGMA'S TWO HEADER CONTROLS ARE DELIBERATELY NOT BUILT
 * ===========================================================================
 * The design's header carries a "My work / Everyone" scope toggle (12:1207) and
 * five period chips — 7D / 30D / QTD / 12M / Custom (12:1212). Both exist to drive
 * the KPI cards and the 12-month billing chart, and the OWNER-RATIFIED dashboard
 * for this phase is chart-free (PHASE2-REPORT §1.3 E2): no KPIs, no sparklines, no
 * deltas, no billing chart.
 *
 * · The **period chips** would then filter nothing. `GET /countries/:cc/events` and
 *   the inquiry list take no period parameter, and stage-counts takes no filter at
 *   all, so every chip would repaint the same screen.
 * · The **scope toggle** is worse than useless rather than merely useless: it could
 *   scope the "needing action" list via `?assigned=`, but `stage-counts` accepts no
 *   `assigned` filter and the activity feed cannot be narrowed to one actor. A
 *   page-level toggle that silently changes one widget out of three reads as a bug.
 *
 * Both are therefore omitted and recorded, rather than rendered dead. They come
 * back with the charts in Phase 5, when there is something for them to filter.
 */
export function PageHeader({
  title,
  subtitle,
  actions,
  badge,
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
  /**
   * PHASE 3a — a SLOT, not a `countryCode` prop.
   *
   * `PageHeader` is shell furniture worn by screens that are not records: the
   * dashboard, both sales lists, the admin pages. Baking a country badge into it
   * would put one on all of them, including the two lists whose subtitle already
   * names the country and whose every ROW now carries its own. A slot lets the
   * one header that IS a record — the quote builder, where no row exists yet —
   * opt in, and the opting-in is visible at the call site instead of hidden in a
   * default.
   */
  badge?: ReactNode;
}): React.JSX.Element {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 px-6 pt-6">
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex min-w-0 flex-wrap items-center gap-2.5">
          {badge}
          <h1 className="text-h1 text-fg">{title}</h1>
        </div>
        {subtitle === undefined ? null : (
          /*
           * WEB TIER A · WT1 — `text-fg`, not `text-fg-secondary`. This slot sits on
           * the BARE CANVAS (#eef1f6), and `text/secondary` #6e7580 measures 4.10:1
           * there — under AA, the mockups' own §7 table marks the pair FORBIDDEN.
           * This is D3's named cost, paid at the shell: on the bare ground a
           * sub-line takes `text/primary` (15.05 on the canvas) and carries its
           * hierarchy by SIZE (Label/12 against the H1) rather than by colour.
           *
           * History of this slot, kept because it keeps happening: Phase 2 shipped
           * it `text-fg-muted` (2.63 ✗, VISUAL-HONESTY §15 D-2), the 2.5a pass
           * raised it to `text-fg-secondary` (4.64 on the then-white page ✓), and
           * the WT1 canvas re-grounded it (4.10 ✗ there) — each fix was correct
           * against the surface of its day. Inside a CARD, `text-fg-secondary`
           * keeps its 4.64/5.85 and stays the quiet layer; the rule is about the
           * bare canvas only.
           */
          <p className="text-label text-fg">{subtitle}</p>
        )}
      </div>
      {actions}
    </div>
  );
}

